import type { AxiosInstance } from "axios";

export type CampaignType = "CPC" | "CPI";
export type CampaignPacingWindow = "HOUR" | "DAY" | "WEEK" | "MONTH";
export type ChannelType = "POS" | "WEB" | "KIOSK";
export type FilterOperator = "IN" | "GREATER_OR_EQUAL";
export type CampaignEligibilityDimension = "STATE" | "MERCHANT_ID";
export type CampaignProductDimension = "BRAND" | "PRICE";

export type DBCampaignStatus = "DRAFT" | "ACTIVE" | "PAUSED" | "ARCHIVED";

export type ComputedCampaignStatus = "SCHEDULED" | "ENDED" | "OUTOFBUDGET";

export type CampaignStatus = DBCampaignStatus | ComputedCampaignStatus;

export type CampaignRule<T> = {
  dimension: T;
  operator: FilterOperator;
  values: string[];
};

type CampaignEligibilityRule = CampaignRule<CampaignEligibilityDimension>;

type CampaignProductRule = CampaignRule<CampaignProductDimension>;

export type CampaignFilter<T> = {
  rules: T[];
} | null;

type CampaignEligibilityFilter = CampaignFilter<CampaignEligibilityRule>;

type CampaignProductFilter = CampaignFilter<CampaignProductRule>;

export function findCampaignRuleValues<D, T extends CampaignRule<D>>(
  filter: CampaignFilter<T>,
  dimension: D,
  operator?: FilterOperator,
) {
  return filter?.rules.find(
    (r) => r.dimension == dimension && (!operator || r.operator == operator),
  )?.values;
}

type CampaignPacingRule = {
  window: CampaignPacingWindow;
  maxCredits: number;
};

export type CampaignAdPlacement = {
  adPlacement: {
    zoneId: string;
    channel: ChannelType;
    title: string | null;
  };
};

type BrandInfo = {
  id: string;
  name: string;
  avatarImageUrl: string | null;
};

export type ApiCampaignRecord = {
  brand: BrandInfo | null;
  id: string;
  name: string;
  budget: number;
  spend: number;
  impressions: number;
  ctr: number;
  status: CampaignStatus;
  active: boolean;
  deleted: boolean;
  type: string;
  timeZone: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  eligibilityFilter: CampaignEligibilityFilter;
  productFilter: CampaignProductFilter;
  pacingRules: CampaignPacingRule[];
  adPlacements: CampaignAdPlacement[];
};

export type ListCampaignsResponse = {
  campaigns: ApiCampaignRecord[];
};

export type ListCampaignsInput = {
  statuses?: CampaignStatus[];
};

export type CreateCampaignEligibilityRule =
  | {
      dimension: "STATE";
      operator: "IN";
      values: [string];
    }
  | {
      dimension: "MERCHANT_ID";
      operator: "IN";
      values: string[];
    };

export type CreateCampaignProductRule =
  | {
      dimension: "BRAND";
      operator: "IN";
      values: [string];
    }
  | {
      dimension: "PRICE";
      operator: "GREATER_OR_EQUAL";
      values: [string];
    };

export type CreateCampaignPacingRule = {
  window: CampaignPacingWindow;
  maxCredits: number;
};

export type CreateCampaignAdPlacement =
  | {
      adPlacementId: string;
    }
  | {
      channel: ChannelType;
      zoneId: string;
    };

export type CreateCampaignInput = {
  name: string;
  type: CampaignType;
  active?: boolean;
  initialBudget?: number;
  timeZone?: string;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  eligibilityFilter: {
    rules: CreateCampaignEligibilityRule[];
  };
  productFilter: {
    rules: CreateCampaignProductRule[];
  };
  pacingRules?: CreateCampaignPacingRule[];
  adPlacements: CreateCampaignAdPlacement[];
};

export type UpdateCampaignInput = {
  active: boolean;
  status: DBCampaignStatus;
};

export type CampaignSummary = {
  id: string;
  name: string;
  budgetMilliDollars: number;
  spendMilliDollars: number;
  impressions: number;
  ctr: number;
  active: boolean;
  status: CampaignStatus;
  updatedAt: string;
  startDate: string | null;
  endDate: string | null;
  stateCode: string | null;
  placementNames: string[];
};

export class CampaignsService {
  constructor(private readonly axios: AxiosInstance) {}

  async list(input: ListCampaignsInput = {}): Promise<ApiCampaignRecord[]> {
    const params = new URLSearchParams();

    input.statuses?.forEach((status) => {
      params.append("status", status);
    });

    const response = await this.axios.get<ListCampaignsResponse>("/campaigns", {
      params,
    });

    return response.data.campaigns;
  }

  async create(input: CreateCampaignInput): Promise<ApiCampaignRecord> {
    const response = await this.axios.post<ApiCampaignRecord>(
      "/campaigns",
      input,
    );

    return response.data;
  }

  async getById(campaignId: string): Promise<ApiCampaignRecord> {
    const response = await this.axios.get<ApiCampaignRecord>(
      `/campaigns/${campaignId}`,
    );

    return response.data;
  }

  async update(
    campaignId: string,
    input: UpdateCampaignInput,
  ): Promise<ApiCampaignRecord> {
    const response = await this.axios.patch<ApiCampaignRecord>(
      `/campaigns/${campaignId}`,
      input,
    );

    return response.data;
  }

  async listSummaries(
    input: ListCampaignsInput = {},
  ): Promise<CampaignSummary[]> {
    const campaigns = await this.list(input);

    return campaigns.map((campaign) => ({
      id: campaign.id,
      name: campaign.name,
      budgetMilliDollars: campaign.budget,
      spendMilliDollars: campaign.spend,
      impressions: campaign.impressions,
      ctr: campaign.ctr,
      active: campaign.active,
      status: campaign.status,
      updatedAt: campaign.updatedAt,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      stateCode: this.getCampaignStateCode(campaign),
      placementNames: this.getCampaignPlacementNames(campaign),
    }));
  }

  private getCampaignStateCode(campaign: ApiCampaignRecord) {
    const stateRule = campaign.eligibilityFilter?.rules.find(
      (rule) => rule.dimension === "STATE" && rule.values.length > 0,
    );

    return stateRule?.values[0] ?? null;
  }

  private getCampaignPlacementNames(campaign: ApiCampaignRecord) {
    return campaign.adPlacements.map(({ adPlacement }) => {
      return adPlacement.title ?? adPlacement.zoneId;
    });
  }
}
